import { default as React, useEffect, useRef } from 'react';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import styled from '@emotion/styled';

import { BundlePurchaseItemList } from '@common/checkout-utils';
import { Drawer } from '@common/overlay-manager';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useIsAuthenticated, useZpayOptionLog } from '@domains/pdp-common';

import { Container } from '../containers/Container';
import { GlobalSizeGuideButton } from '../containers/item/GlobalSizeGuide/GlobalSizeGuideButton';
import { CommonView } from '../containers/options-view/CommonView';
import { OnlyCustomOptionView } from '../containers/options-view/OnlyCustomOptionView';
import { useOptionsControl } from '../hooks/useOptionsControl';
import { useOptions } from '../queries/useOptions';
import { createOrderSheetInPDP } from '../utils/order_sheet_utils';

interface Props {
  extraContent?: React.ReactNode;
  use_portal?: boolean;
  productId?: string;
  open: boolean;
  onEdit?: () => void;
  onClose?: () => void;
  onAddCart?: (bundle_purchase_item_list: BundlePurchaseItemList) => void;
}

export const ZigzagGiftOptionsScreen: React.FC<React.PropsWithChildren<Props>> = ({
  extraContent,
  use_portal = true,
  open,
  onEdit,
  onClose = () => {
    // empty
  },
  onAddCart = () => {
    // empty
  },
}) => {
  const {
    checkedOnlyZigzinOption,
    setCheckedOnlyZigzinOption,
    displayDeliveryTypeToggleButton,
    setDisplayDeliveryTypeToggleButton,
    setIsSingleSelectionAllowed,
    itemCards,
    removeItem,
  } = useOptionsControl();
  const { pageviewUBL } = useZpayOptionLog();
  const { isLoggedIn } = useIsAuthenticated();

  const {
    isFetched,
    options: {
      catalog_product: { id: product_id, product_option_list, custom_input_option_list },
    },
  } = useOptions();
  const purchasedItems = useRef('');
  const drawerOverflow = extraContent ? 'visible' : 'hidden';
  const language = 'ko';

  useEffect(() => {
    setIsSingleSelectionAllowed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!checkedOnlyZigzinOption) {
      setCheckedOnlyZigzinOption(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedOnlyZigzinOption]);

  useEffect(() => {
    if (displayDeliveryTypeToggleButton) {
      setDisplayDeliveryTypeToggleButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayDeliveryTypeToggleButton]);
  /**
   * 1개 이상 아이템이 선택되면, 상품변경 정책으로 인해 기존 아이템을 제거한다.
   */
  useEffect(() => {
    if (itemCards.length > 1) {
      const { item } = itemCards[0];
      removeItem(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCards]);

  useEffect(() => {
    if (!open) {
      return;
    }

    // MEMO: 네이티브 PDP에서는 적재하지 않고 웹 PDP에서만 로그 전송하도록 use_portal 조건 추가
    if (use_portal) {
      pageviewUBL(product_id);
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, use_portal]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (sessionStorage) {
      const items = sessionStorage.getItem(`${product_id}?purchased-items`);

      if (items) {
        purchasedItems.current = items;
      }

      Object.keys(sessionStorage).forEach((key) => {
        if (key.includes('?purchased-items')) {
          sessionStorage.removeItem(key);
        }
      });
    }

    if (isLoggedIn && purchasedItems.current) {
      const purchase = async () => {
        const items = JSON.parse(purchasedItems.current);
        await createOrderSheetInPDP(items);
      };
      purchase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  // TODO: 에러 발생으로 옵션 item list가 없는 경우 대응해야 함 (기획/디자이너단 상의 필요)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!product_id) {
    return null;
  }

  const View = () => {
    const onlyTextOption = !product_option_list.length && custom_input_option_list.length > 0;

    if (onlyTextOption) {
      return <OnlyCustomOptionView isLoggedIn={isLoggedIn} onClose={onClose} onAddCart={onAddCart} />;
    }

    return (
      <CommonView
        disabledPrice
        disabledCounter
        hiddenCounter
        disabledShipping
        disabledConfirm
        disableOrderQuantity
        visibleAdditional={false}
        isLoggedIn={isLoggedIn}
        onClose={onClose}
        onAddCart={onAddCart}
        onEdit={onEdit}
        extraContent={extraContent}
      />
    );
  };

  const render_content = (
    <Container>
      {isFetched ? (
        <PdpSelectOptions className='pdp-select-option'>
          {language !== 'ko' && <GlobalSizeGuideButton />}
          {View()}
        </PdpSelectOptions>
      ) : null}
    </Container>
  );

  if (use_portal) {
    return (
      <Drawer open={open} onClose={onClose} maxHeight={'calc(100vh - 16px)'} height='65vh' overflow={drawerOverflow}>
        {render_content}
      </Drawer>
    );
  }

  return render_content;
};

const PdpSelectOptions = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${vars.color.staticWhite};
  border-radius: 8px 8px 0 0;
  overflow: hidden;
`;
